import { gql } from '@/utilities/gql';
import type { VariablesOf } from '@/utilities/gql';

import { gqlClient } from '@/utilities/graphQLClient';

import { useQuery } from '@tanstack/react-query';

const HOLDING_FRAGMENT = gql(`
  fragment holdingFields on Holding @_unmask {
    id
    symbol
    company {
      name
      uniqueSymbol
      canonicalURL
      info {
        url
      }
      data {
        currencyInfo
      }
    }
    detailedTransactions(startDate: $startDate, endDate: $endDate) {
      transactionDate
      type
      splitRatio
      changeInShares
      changeInCash
      startingUnits
      units
      amount
    }
    detailedReturns(startDate: $startDate, endDate: $endDate) {
      costBasis
      unrealizedGains
      unrealizedGainsPercent
      realizedGains
      realizedGainsPercent
      dividends
      dividendsPercent
      currencyGains
      currencyGainsPercent
      totalGains
      totalGainsPercent
      annualisedReturn
      value
      units
      price
      startDate
      endDate
    }
  }
`);

const QUERY = gql(
  `
  query getPortfolioDetailedReturns(
    $id: String!
    $startDate: String
    $endDate: String
  ) {
    Portfolio(id: $id) {
      id
      type
      manualInputOption
      currencyISO
      detailedReturns(startDate: $startDate, endDate: $endDate) {
        startDate  
        endDate
        value
        costBasis
        unrealizedGains
        unrealizedGainsPercent
        realizedGains
        realizedGainsPercent
        dividends
        dividendsPercent
        currencyGains
        currencyGainsPercent
        totalGains
        totalGainsPercent
        annualisedReturn
      }
      liquidatedHoldings {
        ...holdingFields
      }
      holdings {
        ...holdingFields
      }
    }
  }
`,
  [HOLDING_FRAGMENT]
);

type Args = VariablesOf<typeof QUERY> & {
  updatedAt: string;
};

export const usePortfolioDetailedReturns = (args: Args) => {
  return useQuery({
    queryKey: [QUERY, args],
    queryFn: async () => {
      return await gqlClient.request(QUERY, args);
    },
  });
};

export type PortfolioDetailedReturns = NonNullable<
  ReturnType<typeof usePortfolioDetailedReturns>['data']
>['Portfolio'];
